import React, { ChangeEvent, createRef, useEffect, useState } from "react";
import classnames from "classnames";
import { validate } from "email-validator";

import getRegionComplianceRules from "@/api/requests/consumerApi/getRegionComplianceRules";
import { getDispensary } from "@/api/requests/getDispensary";
import consumerApi from "@/api/services/consumer-api";
import { DISPENSARY_PAGES } from "@/constants/dispensary";
import { Action, Category, Label } from "@/constants/events";
import { Dispensary } from "@/custom-types/Dispensary";
import { useEventTracker } from "@/hooks/useEventTracker";
import { retailerClinicMetaData } from "@/lib/retailerMetadata";
import { useDispatch } from "@/redux/hooks";
import {
  ComplianceRuleset,
  loadComplianceRuleset,
} from "@/redux/slices/complianceRules";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import getDispensaryPrimaryAddress from "@/utils/dispensary/getDispensaryPrimaryAddress";
import generateDispensaryRedirect from "@/utils/generateDispensaryRedirect";
import logError from "@/utils/logError";
import { throwOnRedirectHint } from "@/utils/redirectHints";
import { getCountryCodeHeader } from "@/utils/requestContext";

import Button from "@/components/botanic/Button";
import Input from "@/components/botanic/Input";
import Select from "@/components/botanic/Select";
import DispensaryHeader from "@/components/Dispensary/DispensaryHeader";
import DispensaryTabs from "@/components/Dispensary/DispensaryTabs";
import DispensaryMap from "@/components/Dispensary/Map";
import MetaTags from "@/components/MetaTags";
import Option from "@/components/Option";

const TIMES = [
  "As soon as possible",
  "This Week",
  "Next Week",
  "This Month",
  "Next Month",
];

type AppointmentPageProps = {
  complianceRuleset: ComplianceRuleset;
  dispensary: Dispensary;
};

const AppointmentPage: React.FC<AppointmentPageProps> = ({
  complianceRuleset,
  dispensary,
}) => {
  const dispatch = useDispatch();
  const { publishEvent } = useEventTracker();

  const form = createRef<HTMLFormElement>();

  const [
    disableButtonOnSuccessfulSubmission,
    setdisableButtonOnSuccessfulSubmission,
  ] = useState(false);
  const [emailFieldCompleted, setemailFieldCompleted] = useState(false);
  const [isEmailInvalid, setisEmailInvalid] = useState(false);
  const [isFirstNameInvalid, setisFirstNameInvalid] = useState(false);
  const [isLastNameInvalid, setisLastNameInvalid] = useState(false);
  const [isPhoneInvalid, setisPhoneInvalid] = useState(false);
  const [firstNameFieldCompleted, setFirstNameFieldCompleted] = useState(false);
  const [lastNameFieldCompleted, setLastNameFieldCompleted] = useState(false);
  const [phoneFieldCompleted, setphoneFieldCompleted] = useState(false);
  const [showSubmissionStatus, setshowSubmissionStatus] = useState(false);
  const [submissionIsSuccessful, setsubmissionIsSuccessful] = useState(false);
  const [timeFieldCompleted, settimeFieldCompleted] = useState(false);

  useEffect(() => {
    if (complianceRuleset) {
      dispatch(loadComplianceRuleset(complianceRuleset));
    }
  }, []);

  const validateEmail = (event: ChangeEvent<HTMLInputElement>) => {
    const patternMismatch = !validate(event.target.value);
    setisEmailInvalid(patternMismatch);
    setemailFieldCompleted(!patternMismatch);
  };

  const validatePhone = (event: ChangeEvent<HTMLInputElement>) => {
    const { patternMismatch } = event.target.validity;
    setisPhoneInvalid(patternMismatch);
    setphoneFieldCompleted(!patternMismatch);
  };

  const validateFirstName = (event: ChangeEvent<HTMLInputElement>) => {
    setdisableButtonOnSuccessfulSubmission(false);
    const { valueMissing } = event.target.validity;
    setisFirstNameInvalid(valueMissing);
    setFirstNameFieldCompleted(!valueMissing);
  };

  const validateLastName = (event: ChangeEvent<HTMLInputElement>) => {
    const { valueMissing } = event.target.validity;
    setisLastNameInvalid(valueMissing);
    setLastNameFieldCompleted(!valueMissing);
  };

  const handleSubmit = (event: ChangeEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    submitFormData(formData);
  };

  const onSuccessfulSubmission = () => {
    setemailFieldCompleted(false);
    setFirstNameFieldCompleted(false);
    setLastNameFieldCompleted(false);
    setphoneFieldCompleted(false);
    setshowSubmissionStatus(true);
    setsubmissionIsSuccessful(true);
  };

  const submitFormData = (formData: FormData) => {
    if (isSubmitDisabled) {
      return;
    }

    setdisableButtonOnSuccessfulSubmission(true);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
    let appointmentTime: any;
    const formValues = {};
    for (const formField of formData) {
      // @ts-ignore (fix me please, do not replicate)
      if (formField === "schedule_for") {
        appointmentTime = formField[1];
      }
      // @ts-ignore (fix me please, do not replicate)
      formValues[formField[0]] = formField[1];
    }

    const { first_name, last_name, email, phone, schedule_for } =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
      formValues as any;

    const fullName = `${first_name} ${last_name}`;

    consumerApi
      .post(
        `api/dispensaries/v1/${dispensary.id}/doctor_appointment_request`,
        {
          patient_email: email,
          patient_name: fullName,
          patient_phone: phone,
          schedule_for,
        },
        {
          headers: { "Content-Type": "application/json" },
        },
      )
      .then(() => {
        publishEvent({
          action: Action.click,
          // @ts-ignore -- TODO: Fix publishEvent types (https://leafly.atlassian.net/browse/SHH-1133)
          appointmentTime,
          category: Category.doctorProfile,
          dispensaryId: dispensary.id,
          label: Label.submitAppointment,
        });

        onSuccessfulSubmission();
      })
      .catch((e) => {
        logError(e.message, {
          functionName: "appointment submitFormData",
          service: "consumer",
          statusCode: e.statusCode,
        });

        setshowSubmissionStatus(true);
        setsubmissionIsSuccessful(false);
      });
  };

  const primaryLocation = getDispensaryPrimaryAddress(dispensary);

  const isSubmitDisabled = !(
    firstNameFieldCompleted &&
    lastNameFieldCompleted &&
    emailFieldCompleted &&
    phoneFieldCompleted &&
    timeFieldCompleted &&
    !disableButtonOnSuccessfulSubmission &&
    dispensary.email
  );

  const { coverPhotoUrl, logoUrl, name } = dispensary;
  const { description, image, title } = retailerClinicMetaData(
    coverPhotoUrl,
    logoUrl,
    name,
  );

  return (
    <>
      <MetaTags description={description} image={image} title={title} />
      <DispensaryHeader dispensary={dispensary} />
      <DispensaryTabs
        activeTab={DISPENSARY_PAGES.appointment}
        dispensary={dispensary}
      />
      <div className="container flex flex-col md:flex-row mt-lg gap-lg md:gap-11">
        <div className="w-full">
          {!dispensary.email && (
            <div className="bg-yellow-20 border border-yellow-40 p-lg font-bold text-center">
              Appointment request not available
            </div>
          )}
          <form onSubmit={handleSubmit} ref={form} autoComplete="off">
            <div className="form-field mb-md relative">
              <Input
                label="First name"
                placeholder="First name"
                showPlaceholder
                id="first_name"
                name="first_name"
                onChange={validateFirstName}
                required
              />
              {isFirstNameInvalid && (
                <p className="text-error text-xs">First name is required</p>
              )}
            </div>
            <div className="form-field my-xl relative">
              <Input
                label="Last name"
                placeholder="Last name"
                showPlaceholder
                id="last_name"
                name="last_name"
                onChange={validateLastName}
                required
              />
              {isLastNameInvalid && (
                <p className="text-error text-xs">Last name is required</p>
              )}
            </div>
            <div
              className={classnames("form-field my-xl relative", {
                "text-error": isEmailInvalid,
              })}
            >
              <Input
                label="Email"
                placeholder="Email"
                showPlaceholder
                id="_email"
                name="email"
                onChange={validateEmail}
                required
              />
              {isEmailInvalid && (
                <p className="text-error text-xs">Valid email is required</p>
              )}
            </div>
            <div
              className={classnames("form-field my-xl relative", {
                "text-error": isPhoneInvalid,
              })}
            >
              <Input
                label="Phone number"
                placeholder="Phone number"
                showPlaceholder
                type="tel"
                id="phone"
                name="phone"
                pattern="[0-9]{3}[0-9]{3}[0-9]{4}"
                onChange={validatePhone}
                required
              />
              {isPhoneInvalid && (
                <p className="text-error text-xs">Valid phone is required</p>
              )}
            </div>
            <div>
              <Select
                label="Time"
                name="schedule_for"
                id="select-time"
                defaultValue=""
                className="mb-xl"
                selectClassName="border border-light-grey !px-lg !py-md !text-sm"
                onChange={(event) => {
                  settimeFieldCompleted(event.target.value !== "");
                }}
                required
              >
                <Option value="">Choose time</Option>
                {TIMES.map((time) => (
                  <Option value={time} key={time}>
                    {time}
                  </Option>
                ))}
              </Select>
            </div>
            <Button width="full" type="submit">
              submit
            </Button>
          </form>
          <div className="min-h-[110px] md:mb-xxl mt-lg">
            {!submissionIsSuccessful && showSubmissionStatus && (
              <div className="bg-yellow-20 border border-yellow-40 p-lg font-bold text-center">
                There was a problem submitting. Try again.
              </div>
            )}
            {submissionIsSuccessful && showSubmissionStatus && (
              <div className="bg-green-20 border border-green-40 p-lg font-bold text-center">
                <p className="mb-1">Success!</p>
                <p className="mb-0 font-normal">{`${dispensary.name} will contact you to schedule`}</p>
              </div>
            )}
          </div>
        </div>
        <div className="w-full mb-xxl md:mb-0">
          <div className="text-sm mb-md">{primaryLocation}</div>
          <div className="aspect-square">
            <DispensaryMap location={dispensary.primaryLocation} />
          </div>
        </div>
      </div>
    </>
  );
};

export const getServerSideProps =
  createGetServerSideProps<AppointmentPageProps>(async (context) => {
    const { req, query, resolvedUrl } = context;
    const { slug } = query;

    const dispensary = await throwOnRedirectHint(
      () => getDispensary(slug as string),
      slug as string,
    );

    const domainCountryCode = getCountryCodeHeader(req.headers);

    const redirect = generateDispensaryRedirect(
      domainCountryCode,
      resolvedUrl,
      query,
      dispensary,
    );

    if (redirect) {
      return { redirect };
    }

    // fallback if redirect not generated
    if (!dispensary) {
      return {
        notFound: true,
      };
    }

    const complianceRuleset = await getRegionComplianceRules(
      dispensary.state,
      dispensary.country,
    );

    return {
      props: {
        complianceRuleset,
        dispensary,
      },
    };
  });

export default AppointmentPage;
